// src/auth/renewToken.js
import { graphRequest } from "../../authConfig";

let refreshPromise = null;
let logoutInProgress = false;

// Guard: if logout redirect hasn't completed within 10 s, reset the flag so
// the next attempt can retry rather than silently getting stuck forever.
const LOGOUT_TIMEOUT_MS = 10_000;

function clearAppSession() {
  sessionStorage.clear();
}

async function forceLogout(instance) {
  if (logoutInProgress) return null;
  logoutInProgress = true;

  clearAppSession();

  // Reset the guard after timeout so a stuck logout doesn't freeze the app.
  const resetTimer = setTimeout(() => {
    logoutInProgress = false;
  }, LOGOUT_TIMEOUT_MS);

  try {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (err) {
    console.error("Logout redirect failed:", err);
    clearTimeout(resetTimer);
    logoutInProgress = false; // allow retry on next request
    window.location.href = "/";
  }

  return null;
}

export async function getValidAccessToken(instance) {
  if (!instance) {
    throw new Error("MSAL instance is not available.");
  }

  let account = instance.getActiveAccount();

  if (!account) {
    const accounts = instance.getAllAccounts() || [];
    if (accounts.length === 1) {
      account = accounts[0];
      instance.setActiveAccount(account);
    }
  }

  if (!account) {
    console.warn("No active account → logout");
    return forceLogout(instance);
  }

  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const resp = await instance.acquireTokenSilent({
        ...graphRequest,
        account,
      });

      if (!resp?.accessToken) {
        console.warn("No token returned → logout");
        return forceLogout(instance);
      }

      return resp.accessToken;
    } catch (e) {
      // 🔴 THIS IS YOUR RULE: ANY FAILURE = logout
      console.error("Token call failed (treated as non-200):", e);
      return forceLogout(instance);
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}
